import axios from 'axios';
import { axiosInstance } from './axios';

interface LoginResponse {
    accessToken: string;
    refreshToken: string;
}

interface UserResponse {
    id: string;
    username: string;
    email: string;
    firstName: string;
    lastName: string;
    isActive: boolean;
    createdAt: string;
    lastLoginAt: string | null;
    updatedAt: string;
}

// Store tokens in localStorage
const storeTokens = (accessToken: string, refreshToken: string) => {
    localStorage.setItem('accessToken', accessToken);
    localStorage.setItem('refreshToken', refreshToken);
};

// Remove tokens from localStorage
const removeTokens = () => {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
};

// Get stored tokens
const getAccessToken = () => localStorage.getItem('accessToken');
const getRefreshToken = () => localStorage.getItem('refreshToken');

// Check if user is authenticated and token is not expired
const isAuthenticated = () => {
    const token = getAccessToken();
    if (!token) return false;

    try {
        // Decode the JWT token
        const payload = JSON.parse(atob(token.split('.')[1]));
        // Check if token is expired
        const expiry = payload.exp * 1000; // Convert to milliseconds
        return expiry > Date.now();
    } catch (error) {
        console.error('Error checking token expiration:', error);
        return false;
    }
};

// Login user
const login = async (username: string, password: string): Promise<boolean> => {
    console.log('[AUTH] Login attempt for user:', username);

    // Prepare login data
    const loginData = {
        username,
        password
    };
    
    try {
        const response = await axiosInstance.post<LoginResponse>(
            '/auth/login',
            loginData
        );

           if (!response.data || !response.data.accessToken) {
            console.error('[AUTH] Login failed - No token in response');
            return false;
        }

        const { accessToken, refreshToken } = response.data;
        storeTokens(accessToken, refreshToken);
        console.log('[AUTH] Tokens stored in localStorage');
        return true;
    } catch (error) {
        if (axios.isAxiosError(error)) {
            console.error('[AUTH] Login failed - Axios error:', {
                status: error.response?.status,
                statusText: error.response?.statusText,
                data: error.response?.data,
                url: error.config?.url,
                method: error.config?.method,
                headers: error.config?.headers
            });

            return false;
        } else {
            console.error('[AUTH] Login failed - Non-Axios error:', error);
        }
        return false;
    }
};

// Logout user
const logout = async (): Promise<boolean> => {
    try {
        const accessToken = getAccessToken();
        if (accessToken) {
            await axiosInstance.post('/auth/logout', {}, {
                headers: {
                    Authorization: `Bearer ${accessToken}`
                }
            });
        }
        removeTokens();
        return true;
    } catch (error) {
        console.error('Logout failed:', error);
        removeTokens(); // Still remove tokens even if server logout fails
        return true;
    }
};

// Refresh token
const refreshToken = async (): Promise<boolean> => {
    try {
        const accessToken = getAccessToken();
        const refreshTokenValue = getRefreshToken();

        if (!accessToken || !refreshTokenValue) {
            return false;
        }

        const response = await axiosInstance.post<LoginResponse>(
            '/auth/refresh-token',
            {
                accessToken,
                refreshToken: refreshTokenValue
            }
        );

        const { accessToken: newAccessToken, refreshToken: newRefreshToken } = response.data;
        storeTokens(newAccessToken, newRefreshToken);
        return true;
    } catch (error) {
        console.error('Token refresh failed:', error);
        removeTokens(); // Remove tokens if refresh fails
        return false;
    }
};

// Get current user
const getCurrentUser = async (): Promise<UserResponse | null> => {
    try {
        const accessToken = getAccessToken();
        if (!accessToken) {
            return null;
        }

        // In a real app, you would have an endpoint like /Auth/me or /Auth/current-user
        // For now, we'll simulate by decoding the JWT
        const tokenPayload = JSON.parse(atob(accessToken.split('.')[1]));

        const usernameFromToken = tokenPayload.unique_name || tokenPayload.name;

        return {
            id: tokenPayload.nameid,
            username: usernameFromToken,
            email: tokenPayload.email,
            firstName: tokenPayload.firstName,
            lastName: tokenPayload.lastName,
            isActive: true,
            createdAt: new Date().toISOString(),
            lastLoginAt: null,
            updatedAt: new Date().toISOString()
        };
    } catch (error) {
        console.error('Get current user failed:', error);
        return null;
    }
};

// Setup axios interceptor for adding auth token
const setupAxiosInterceptors = () => {
    axiosInstance.interceptors.request.use(
        (config) => {
            const token = getAccessToken();
            if (token) {
                config.headers['Authorization'] = `Bearer ${token}`;
            }
            return config;
        },
        (error) => {
            return Promise.reject(error);
        }
    );

    axiosInstance.interceptors.response.use(
        (response) => response,
        async (error) => {
            const originalRequest = error.config;

            // If 401 error and we haven't tried to refresh the token yet
            if (error.response?.status === 401 && !originalRequest._retry) {
                originalRequest._retry = true;

                const refreshSuccessful = await refreshToken();
                if (refreshSuccessful) {
                    const token = getAccessToken();
                    axiosInstance.defaults.headers.common['Authorization'] = `Bearer ${token}`;
                    return axiosInstance(originalRequest);
                } else {
                    // If refresh failed, redirect to login
                    window.location.href = '/login';
                    return Promise.reject(error);
                }
            }

            return Promise.reject(error);
        }
    );
};

// Export the auth service
const authService = {
    login,
    logout,
    refreshToken,
    isAuthenticated,
    getAccessToken,
    getCurrentUser,
    setupAxiosInterceptors
};

export default authService;
